import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Calculator, Plus, Trash, TrendUp, TrendDown, CurrencyDollar, ChartLineUp } from "@phosphor-icons/react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import DatePicker from "../components/DatePicker";

const CATEGORIES = [
  { key: "cogs", label: "Cost of Goods", color: "#E07A5F" },
  { key: "labor", label: "Labor", color: "#F4A261" },
  { key: "rent", label: "Rent", color: "#2F4F4F" },
  { key: "utilities", label: "Utilities", color: "#81B29A" },
  { key: "marketing", label: "Marketing", color: "#3D405B" },
  { key: "supplies", label: "Supplies", color: "#6D6875" },
  { key: "other", label: "Other", color: "#A8A29E" },
];

export default function AccountingPage() {
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [days, setDays] = useState(30);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), category: "cogs", subcategory: "", amount: "", vendor: "", notes: "", invoice_number: "" });
  const [filterCat, setFilterCat] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([
        api.get(`/accounting/summary?days=${days}`),
        api.get(`/accounting/expenses?days=${days}${filterCat ? `&category=${filterCat}` : ""}`),
      ]);
      setSummary(s.data);
      setExpenses(e.data);
    } catch { /* silent */ }
  }, [days, filterCat]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async () => {
    if (!form.amount || !form.date) { toast.error("Date and amount are required"); return; }
    try {
      await api.post("/accounting/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Expense added");
      setShowAdd(false);
      setForm({ date: new Date().toISOString().slice(0, 10), category: "cogs", subcategory: "", amount: "", vendor: "", notes: "", invoice_number: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const removeExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/accounting/expenses/${id}`); toast.success("Removed"); fetchAll(); }
    catch { toast.error("Failed"); }
  };

  const pieData = summary ? CATEGORIES.map(c => ({ name: c.label, value: summary.by_category[c.key] || 0, color: c.color })).filter(d => d.value > 0) : [];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="accounting-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Calculator size={24} weight="duotone" style={{ color: "#2F4F4F" }} />
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Accounting</h2>
          <Badge className="bg-amber-100 text-amber-700 text-[10px]">P&L</Badge>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="text-xs border border-stone-200 rounded-md px-2 py-1.5 bg-white" data-testid="period-select">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button onClick={() => setShowAdd(true)} style={{ backgroundColor: "#2F4F4F" }} data-testid="add-expense-btn">
            <Plus size={14} className="mr-1" /> Expense
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KPI icon={CurrencyDollar} label="Revenue" value={`$${summary.total_revenue.toFixed(2)}`} color="#81B29A" testId="kpi-revenue" />
          <KPI icon={TrendDown} label="Expenses" value={`$${summary.total_expenses.toFixed(2)}`} color="#E07A5F" testId="kpi-expenses" />
          <KPI icon={summary.net_profit >= 0 ? TrendUp : TrendDown} label="Net Profit" value={`$${summary.net_profit.toFixed(2)}`} color={summary.net_profit >= 0 ? "#2F4F4F" : "#E07A5F"} sub={`${summary.profit_margin}% margin`} testId="kpi-profit" />
          <KPI icon={ChartLineUp} label="COGS / Labor %" value={`${summary.cogs_pct}% / ${summary.labor_pct}%`} color="#3D405B" sub="Industry: 30% / 30%" testId="kpi-ratios" />
        </div>
      )}

      {/* Charts */}
      {summary && summary.daily.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2 bg-white rounded-lg border border-stone-200 p-4">
            <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Daily Revenue vs Expenses</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={summary.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="revenue" stroke="#81B29A" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="expenses" stroke="#E07A5F" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="profit" stroke="#2F4F4F" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-lg border border-stone-200 p-4">
            <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Expense Breakdown</h3>
            {pieData.length === 0 ? (
              <div className="h-[240px] flex items-center justify-center text-xs text-stone-400">No expenses logged</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value">
                    {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* Expense List */}
      <div className="bg-white rounded-lg border border-stone-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Expenses</h3>
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="text-xs border border-stone-200 rounded-md px-2 py-1 bg-white" data-testid="filter-category">
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        {expenses.length === 0 ? (
          <p className="text-center text-xs text-stone-400 py-6">No expenses logged yet. Add one to start tracking your P&L.</p>
        ) : (
          <div className="space-y-1.5">
            {expenses.map((e) => {
              const cat = CATEGORIES.find(c => c.key === e.category) || CATEGORIES[CATEGORIES.length - 1];
              return (
                <div key={e.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-stone-50 transition-colors" data-testid={`expense-${e.id}`}>
                  <div className="w-1 h-10 rounded-full" style={{ backgroundColor: cat.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-stone-800">{e.vendor || cat.label}</span>
                      <Badge variant="outline" className="text-[9px]">{cat.label}</Badge>
                      {e.subcategory && <span className="text-[10px] text-stone-500">· {e.subcategory}</span>}
                    </div>
                    <p className="text-[10px] text-stone-400">{e.date} {e.invoice_number ? `· INV ${e.invoice_number}` : ""} {e.notes ? `· ${e.notes}` : ""}</p>
                  </div>
                  <span className="text-sm font-semibold text-stone-800">${parseFloat(e.amount).toFixed(2)}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeExpense(e.id)} className="h-7 w-7 p-0 text-red-400" data-testid={`delete-expense-${e.id}`}>
                    <Trash size={12} />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-expense-dialog">
          <DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Date</Label>
                <div className="mt-1">
                  <DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="expense-date" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Amount ($)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" data-testid="expense-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full border border-stone-200 rounded-md px-2 py-2 text-sm" data-testid="expense-category">
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Subcategory</Label>
                <Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} placeholder="e.g. food cost" className="mt-1" data-testid="expense-subcategory" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Vendor</Label>
                <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Supplier name" className="mt-1" data-testid="expense-vendor" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Invoice #</Label>
              <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="mt-1" data-testid="expense-invoice" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" data-testid="expense-notes" />
            </div>
            <Button onClick={submit} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="save-expense-btn">Save Expense</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, sub, testId }) {
  return (
    <div className="bg-white rounded-lg border border-stone-200 p-3" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} weight="duotone" style={{ color }} />
        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{label}</span>
      </div>
      <p className="text-lg font-semibold" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] text-stone-400 mt-0.5">{sub}</p>}
    </div>
  );
}
